import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '#/components/Box/Box';
import type { CmsApiPage } from '#/modules/cms/api/types/CmsApiPage';
import type { CmsApiPersonaQuizBlockData } from '#/modules/cms/api/types/blocks/CmsApiPersonaQuizBlock';
import { scrollIntoView } from '#/utils/scrollIntoView';

import { CmsPersonaQuizBlockQuestion } from './CmsPersonaQuizBlockQuestion';
import { CmsPersonaQuizBlockResult } from './CmsPersonaQuizBlockResult';

const uuid = () => Math.random().toString(36).slice(2);

type Props = {
  blockIndex: number;
  data: CmsApiPersonaQuizBlockData;
  content?: CmsApiPage;
};

export function CmsPersonaQuizBlock({
  blockIndex,
  data: { questions, results },
  content,
}: Props) {
  const [score, setScore] = useState(Array(questions.length).fill([]));
  const [result, setResult] = useState(-1);
  const [currentQuestion, setCurrentQuestion] = useState(-1);
  const [version, setVersion] = useState(uuid());
  const resultRef = useRef(null);
  const currentQuestionRef = useRef(null);

  const restart = () => {
    setResult(-1);
    setCurrentQuestion(0);
    setScore(Array(questions.length).fill([]));
    setVersion(uuid());
  };

  const updateScore = (index: number, points: number[]) => {
    const newScore = [...score];
    newScore[index] = points;
    setScore(newScore);
  };

  const getResult = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    (finalScore: any) => {
      const tally = results.map((res, resultIndex) =>
        finalScore.reduce(
          // oxlint-disable-next-line typescript/no-explicit-any
          (acc: any, question: any) => acc + question[resultIndex],
          0,
        ),
      );
      const persona = tally.reduce(
        (acc, total, index) => {
          if (total > acc.count) {
            return {
              count: total,
              index,
            };
          }

          return acc;
        },
        {
          count: 0,
          index: 0,
        },
      );
      return persona.index;
    },
    [results],
  );
  useEffect(() => {
    if (!score.some((question) => question.length === 0)) {
      const finalResult = getResult(score);
      setResult(finalResult);
    } else if (score.some((question) => question.length > 0)) {
      const nextQuestion = score.findIndex((question) => question.length === 0);
      setCurrentQuestion(nextQuestion);
    }
  }, [currentQuestion, getResult, score]);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (currentQuestionRef && currentQuestionRef.current) {
      scrollIntoView(currentQuestionRef.current);
    }
  }, [currentQuestion]);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (resultRef && resultRef.current) {
      scrollIntoView(resultRef.current);
    }
  }, [result]);
  return (
    <Box qaId="cms-persona-quiz" key={version}>
      {questions.map((question, index) => (
        <div
          ref={index === currentQuestion ? currentQuestionRef : null} // oxlint-disable-next-line react/no-array-index-key
          key={index}
        >
          <CmsPersonaQuizBlockQuestion
            blockIndex={blockIndex}
            question={question}
            index={index}
            updateScore={updateScore}
            answered={score[index].length > 0}
          />
        </div>
      ))}
      {result > -1 && (
        <Box mt={64}>
          <div ref={resultRef}>
            <CmsPersonaQuizBlockResult
              blockIndex={blockIndex}
              result={results[result]}
              clickRetake={restart}
              content={content}
            />
          </div>
        </Box>
      )}
    </Box>
  );
}
